import { PhysicalType } from "@src/core/engine/units/constants";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getFormatMaxDecimals } from "../helpers/number";
import { UnitConversionFn, useUnitConversion } from "./useUnitConversion";

type ConvertedValueFormatOptions = {
  excludeSymbol?: boolean;
  targetDecimals?: number | null;
};

export function useConvertedValueFormat() {
  const { i18n } = useTranslation();
  const { getUnitSymbol } = useUnitConversion();

  return useCallback(
    (
      value: string | null,
      physicalType: PhysicalType | null,
      toUserUnit: UnitConversionFn,
      {
        excludeSymbol = false,
        targetDecimals,
      }: ConvertedValueFormatOptions = {},
    ): string => {
      if (!value) return "";

      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) return value;

      if (!physicalType)
        return Intl.NumberFormat(i18n.language).format(numericValue);

      const convertedValue = toUserUnit(numericValue, physicalType);
      const symbol = excludeSymbol ? "" : ` ${getUnitSymbol(physicalType)}`;
      const maxDecimals = getFormatMaxDecimals(convertedValue, targetDecimals);
      return `${Intl.NumberFormat(i18n.language, {
        maximumFractionDigits: maxDecimals,
      }).format(convertedValue)}${symbol}`;
    },
    [getUnitSymbol, i18n.language],
  );
}
